import React, { useEffect, useState, useCallback, useRef, useLayoutEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Terminal, Clock, Info, Cpu, Brain, ChevronRight, ChevronLeft, Search, X, Copy, Check,
  FileText, Table, Globe, Code, HelpCircle, CheckCircle2, Share2, Zap, Users, Bot, Wrench, Headphones, GitBranch, MessagesSquare
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import ApiClient from '../utils/api';
import EmptyAnalyticsState from '../components/EmptyAnalyticsState';
import { Avatar, Badge, Button, cn } from '../components/ui';
import { ChannelIcon } from '../components/live-chat/ChatList';
import { CHANNEL_LABELS, channelOf, dayLabel, shortAgo, splitQuickReplies, toMs } from '../utils/liveChat';


function TraceTreeNode({ node, index, expandedNodes, toggleNode, fmtLatency, prettyJson }) {
  const hasChildren = node.children && node.children.length > 0;
  const isExpanded = expandedNodes.has(node.node_id);

  const renderContent = (content) => {
    if (!content) return null;
    if (typeof content === 'string') {
      try {
        const parsed = JSON.parse(content);
        return <pre className="text-[10px] bg-zinc-50 p-3 rounded-lg whitespace-pre-wrap break-words font-mono text-zinc-600 leading-relaxed">{JSON.stringify(parsed, null, 2)}</pre>;
      } catch (e) {
        return <p className="text-[11px] text-zinc-600 leading-relaxed break-words">{content}</p>;
      }
    }
    return <pre className="text-[10px] bg-zinc-50 p-3 rounded-lg whitespace-pre-wrap break-words font-mono text-zinc-600 leading-relaxed">{JSON.stringify(content, null, 2)}</pre>;
  };

  const getDisplayContent = (type) => {
    if (node.input_parsed) return node.input_parsed;
    const attrs = node.attributes || {};
    const meta = node.metadata || {};
    // Check common LLM/Tool/Agent keys
    const val = attrs.input_content || attrs.prompt || attrs.input || attrs.tool_input || attrs.arguments || attrs.value || meta.input;
    if (val) return val;
    // Fallback: search for any key containing 'input' or 'prompt'
    const fallbackKey = Object.keys(attrs).find(k => k.toLowerCase().includes('input') || k.toLowerCase().includes('prompt'));
    return fallbackKey ? attrs[fallbackKey] : null;
  };

  const getOutputContent = (type) => {
    if (node.output_parsed) return node.output_parsed;
    const attrs = node.attributes || {};
    const meta = node.metadata || {};
    const val = attrs.output_content || attrs.response || attrs.output || attrs.tool_output || attrs.result || attrs.completion || meta.output;
    if (val) return val;
    // Fallback: search for any key containing 'output', 'response', or 'result'
    const fallbackKey = Object.keys(attrs).find(k => k.toLowerCase().includes('output') || k.toLowerCase().includes('response') || k.toLowerCase().includes('result') || k.toLowerCase().includes('completion'));
    return fallbackKey ? attrs[fallbackKey] : null;
  };
  const type = node.type?.toLowerCase();

  const getIcon = () => {
    const name = (node.name || '').toLowerCase();

    // 1. Specialized Agents
    if (name.includes('pdf_specialist')) return <FileText size={16} className="text-rose-500" />;
    if (name.includes('docx_specialist')) return <FileText size={16} className="text-blue-500" />;
    if (name.includes('csv_specialist')) return <Table size={16} className="text-emerald-500" />;
    if (name.includes('web_specialist')) return <Globe size={16} className="text-sky-500" />;
    if (name.includes('api_specialist')) return <Code size={16} className="text-purple-500" />;
    if (name.includes('faq_specialist')) return <HelpCircle size={16} className="text-amber-500" />;
    if (name.includes('validation_specialist')) return <CheckCircle2 size={16} className="text-emerald-600" />;

    // 2. Core Actions
    if (name.includes('delegate_task_to_member')) return <Share2 size={16} className="text-indigo-500" />;
    if (name.includes('openaichat.ainvoke_stream')) return <Zap size={16} className="text-amber-500" />;

    // 3. Lead Agent / Generic Runs
    if (name.includes('ai_support_team')) return <Users size={16} className="text-indigo-600" />;
    if (name.includes('.run')) return <Bot size={16} className="text-blue-600" />;

    // 4. Tools & Fallbacks
    if (type === 'tool' || name.includes('tool')) return <Wrench size={16} className="text-zinc-600" />;

    if (type === 'llm') return <Cpu size={16} className="text-blue-500" />;
    if (type === 'agent') return <Brain size={16} className="text-indigo-500" />;
    return <Terminal size={16} className="text-zinc-500" />;
  };

  return (
    <div className="space-y-2 animate-fadeIn">
      <div
        onClick={() => toggleNode(node.node_id)}
        className={`convo-trace-item px-3 py-2 rounded-lg border bg-white transition-colors cursor-pointer ${
          isExpanded ? 'border-zinc-400' : 'border-zinc-200 hover:border-zinc-300'
          }`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-7 h-7 rounded-md bg-zinc-50 border border-zinc-200 flex items-center justify-center overflow-hidden flex-shrink-0">
              {getIcon()}
            </div>
            <span className="text-xs font-medium text-zinc-800 break-all">{node.name}</span>
          </div>
          <div className="flex items-center gap-3 text-[11px] text-zinc-400 tabular-nums">
            <div className="flex items-center gap-1">
              <Clock size={10} />
              {fmtLatency(node.latency_ms)}
            </div>
            {hasChildren && (
              <div className={`transition-transform duration-200 ${isExpanded ? 'rotate-90 text-zinc-700' : ''}`}>
                <ChevronRight size={14} />
              </div>
            )}
          </div>
        </div>

        {isExpanded && (
          <div className="mt-3 space-y-4 pt-3 border-t border-zinc-100">
            <div className="flex flex-col gap-6">
              <div className="space-y-2">
                <div className="flex items-center gap-2 px-1">
                  <div className="w-1.5 h-1.5 rounded-full bg-zinc-400" />
                  <span className="text-[11px] font-medium text-zinc-500">Input</span>
                </div>
                {renderContent(getDisplayContent(type))}
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2 px-1">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  <span className="text-[11px] font-medium text-zinc-500">Output</span>
                </div>
                {renderContent(getOutputContent(type))}
              </div>
            </div>

            {node.metadata && Object.keys(node.metadata).length > 0 && (
              <div className="space-y-2">
                <span className="text-[11px] font-medium text-zinc-500 px-1">Metadata</span>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(node.metadata).map(([k, v]) => (
                    <div key={k} className="px-2 py-1 bg-zinc-100 rounded text-[10px] text-zinc-600">
                      <span className="text-zinc-400 mr-1">{k}:</span> {String(v)}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {isExpanded && hasChildren && (
        <div className="convo-subtree-inner relative ml-4 pl-6 space-y-2 mt-2">
          {node.children.map((child, i) => (
            <div key={child.node_id} className="relative">
              {/* L-shaped connector - extended top to join prev */}
              <div className="absolute -left-6 -top-3 bottom-1/2 w-6 border-l border-b border-zinc-200 rounded-bl-xl" />
              {/* Continued vertical line for siblings - extended bottom to join next */}
              {i < node.children.length - 1 && (
                <div className="absolute -left-6 top-1/2 -bottom-3 w-6 border-l border-zinc-200" />
              )}

              <TraceTreeNode
                node={child}
                index={i}
                expandedNodes={expandedNodes}
                toggleNode={toggleNode}
                fmtLatency={fmtLatency}
                prettyJson={prettyJson}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const FANCY_NAMES = [
  'Alistair', 'Augustus', 'Beauregard', 'Cedric', 'Evander',
  'Maximilian', 'Montgomery', 'Remington', 'Sebastian', 'Anastasia',
  'Arabella', 'Celeste', 'Cordelia', 'Evangeline', 'Genevieve',
  'Isadora', 'Seraphina', 'Valentina'
];

export const getFancyName = (id) => {
  if (!id) return FANCY_NAMES[0];
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = (hash << 5) - hash + id.charCodeAt(i);
    hash |= 0;
  }
  return FANCY_NAMES[Math.abs(hash) % FANCY_NAMES.length];
};

const resolveDisplayName = (userId, sessionId) => {
  const normalized = (userId || '').trim().toLowerCase();
  if (!normalized || ['guest', 'guest user', 'unknown', 'none', 'null', 'undefined', 'anonymous'].includes(normalized)) {
    return getFancyName(sessionId);
  }
  return userId;
};




const renderMessageText = (text) => {
  if (!text) return null;

  if (typeof text !== 'string') {
    if (text && typeof text === 'object') {
      if (text.answer) return text.answer;
      return JSON.stringify(text);
    }
    return String(text);
  }
  
  let cleanText = text;
  cleanText = cleanText.replace(/::IMAGE::(\S+)/g, 'IMAGE_REF:$1');
  
  const parts = cleanText.split(/(IMAGE_REF:[^\s<"')]+)/g);
  return parts.map((part, index) => {
    if (part.startsWith('IMAGE_REF:')) {
      const path = part.substring(10);
      let cleanPath = path;
      let fullUrl = cleanPath;
      if (cleanPath.startsWith('/uploads')) {
        const apiBase = process.env.REACT_APP_API_BASE_URL || '';
        const baseDomain = apiBase.endsWith('/api') ? apiBase.slice(0, -4) : apiBase;
        fullUrl = `${baseDomain}${cleanPath}`;
      }
      return (
        <img
          key={index}
          src={fullUrl}
          alt="Visual Context"
          style={{
            maxWidth: '100%',
            maxHeight: '300px',
            borderRadius: '12px',
            marginTop: '8px',
            display: 'block',
            boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
          }}
        />
      );
    }
    return <span key={index}>{part}</span>;
  });
};

export default function AnalyticsConversations({ activeAiAgentId, contactSessionIds, isContactDetail = false }) {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [, setError] = useState(null);
  const [searchParams] = useSearchParams();
  const sessionIdParam = searchParams.get('session_id');
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionTraces, setSessionTraces] = useState({});
  const [expandedTraceNodes, setExpandedTraceNodes] = useState(new Set());
  const [convoShownTraceIds, setConvoShownTraceIds] = useState(new Set());
  const [welcomeMessage, setWelcomeMessage] = useState('');
  const [showParams, setShowParams] = useState(false);
  const [convoPage, setConvoPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState(sessionIdParam || '');
  const [debouncedSearch, setDebouncedSearch] = useState(sessionIdParam || '');
  const itemsPerPage = 10;
  // the two panels fill the window down to an 8px bottom gap (like the live chat screen), so the page does not scroll
  const panelsRef = useRef(null);
  const [panelsHeight, setPanelsHeight] = useState(null);
  useLayoutEffect(() => {
    if (isContactDetail) return undefined;
    const fit = () => {
      const el = panelsRef.current;
      if (!el) return;
      const scroller = el.closest('main');
      const offset = el.getBoundingClientRect().top + (scroller ? scroller.scrollTop : 0);
      setPanelsHeight(Math.max(420, Math.floor(window.innerHeight - offset - 8)));
    };
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  });
  const formatTime = (epoch) => {
    if (!epoch) return '';
    const timestamp = epoch < 10000000000 ? epoch * 1000 : epoch;
    return new Date(timestamp).toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: 'Asia/Kolkata'
    });
  };

  const fetchData = useCallback(async () => {
    if (contactSessionIds && contactSessionIds.length === 0) {
      setData({ sessions: [], total_count: 0 });
      return;
    }
    setIsLoading(true);
    try {
      const sessionIdsParam = contactSessionIds ? `&session_ids=${encodeURIComponent(contactSessionIds.join(','))}` : '';
      const res = await ApiClient.request(
        `/analytics/dashboard/conversations?page=${convoPage}&page_size=${itemsPerPage}&search=${encodeURIComponent(debouncedSearch)}${sessionIdsParam}`,
        {
          headers: { 'X-AI-Agent-ID': activeAiAgentId }
        }
      );
      setData(res);
      if (res?.welcome_message) {
        setWelcomeMessage(res.welcome_message);
      }
      setError(null);
    } catch (err) {
      console.error('[AnalyticsConversations] Fetch error:', err);
      setError(err.message || 'Failed to sync with conversation analytics');
    } finally {
      setIsLoading(false);
    }
  }, [activeAiAgentId, convoPage, debouncedSearch, contactSessionIds]);

  const fetchSessionTraces = useCallback(async (sessionId) => {
    try {
      const data = await ApiClient.request(`/analytics/traces/${sessionId}`, {
        headers: { 'X-AI-Agent-ID': activeAiAgentId }
      });
      setSessionTraces(prev => ({ ...prev, [sessionId]: data.traces || [] }));
    } catch (error) {
      console.error("Error fetching traces:", error);
    }
  }, [activeAiAgentId]);

  useEffect(() => {
    const mainEl = document.querySelector('main');
    if (mainEl) {
      mainEl.scrollTop = 0;
    }
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    setSearchTerm(sessionIdParam || '');
    setDebouncedSearch(sessionIdParam || '');
    setConvoPage(1);
    setSelectedSession(null);
  }, [activeAiAgentId, sessionIdParam]);

  // Auto-select session if sessionIdParam matches any loaded session
  useEffect(() => {
    if (sessionIdParam && data?.sessions) {
      const match = data.sessions.find(s => s.session_id === sessionIdParam);
      if (match) {
        setSelectedSession(match);
      }
    }
  }, [data, sessionIdParam]);

  useEffect(() => {
    if (activeAiAgentId) {
      fetchData();
      if (!welcomeMessage) {
        ApiClient.getWidgetConfig()
          .then(cfg => {
            if (cfg && (cfg.welcome_message || cfg.welcomeMessage)) {
              setWelcomeMessage(cfg.welcome_message || cfg.welcomeMessage);
            }
          })
          .catch(() => {});
      }
    }
  }, [activeAiAgentId, fetchData, welcomeMessage]);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setConvoPage(1); // Reset to first page on search
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (selectedSession && !sessionTraces[selectedSession.session_id]) {
      fetchSessionTraces(selectedSession.session_id);
    }
  }, [selectedSession, sessionTraces, fetchSessionTraces]);

  const toggleTraceNode = (nodeId, e) => {
    if (e) e.stopPropagation();
    setExpandedTraceNodes(prev => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  };

  const fmtLatency = (ms) => {
    if (ms === null || ms === undefined) return '';
    if (ms < 1000) return `${Math.round(ms)}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const prettyJson = (val) => {
    if (!val) return 'null';
    if (typeof val === 'string') {
      try {
        return JSON.stringify(JSON.parse(val), null, 2);
      } catch {
        return val;
      }
    }
    return JSON.stringify(val, null, 2);
  };

  const isTraceMatch = (node, run) => {
    if (!node || !run) return false;

    // ⭐ ELITE: Prioritize ID-based matching (100% accurate)
    // We ensure we match the ROOT node of the execution (where parent_node_id is null or 'None')
    const isRoot = !node.parent_node_id || node.parent_node_id === 'None' || node.parent_node_id === '';
    if (run.run_id && node.run_id && run.run_id === node.run_id && isRoot) return true;

    // Fallback: Legacy fuzzy matching logic
    // Robust normalization: Strip markdown syntax (**), newlines (\n), punctuation, and extra whitespace
    const normalize = (str) => {
      if (!str) return '';
      let s = typeof str === 'string' ? str : JSON.stringify(str);
      return s.toLowerCase()
        .replace(/\\n/g, ' ')
        .replace(/\*\*/g, '')
        .replace(/[^a-z0-9\s]/g, '')
        .trim()
        .replace(/\s+/g, ' ');
    };
    const runIn = normalize(run.input_content);
    const runOut = normalize(run.assistant_content);
    const nodeIn = normalize(node.input_parsed);

    let nodeOut = normalize(node.output_parsed);

    if (isRoot) {
      if (runOut.length > 5 && (nodeOut.includes(runOut) || runOut.includes(nodeOut))) return true;
      if (runIn.length > 2 && (nodeIn.includes(runIn) || runIn.includes(nodeIn))) return true;
    }
    if (runIn.length > 2 && nodeIn.includes(runIn)) return true;
    return false;
  };

  if (isLoading && !data) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-5 sm:pb-6 border-b border-zinc-100 gap-3 sm:gap-4">
          <div>
            <div className="h-6 w-44 bg-zinc-200 rounded" />
            <div className="h-3 w-72 bg-zinc-100 rounded mt-2" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-1 bg-white rounded-xl border border-zinc-200 p-4 space-y-3 h-[600px]">
            <div className="h-9 w-full bg-zinc-100 rounded-lg" />
            <div className="space-y-2 pt-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="p-4 rounded-lg bg-zinc-50/70 border border-zinc-100 space-y-2 h-[100px] flex flex-col justify-center">
                  <div className="h-3.5 w-3/4 bg-zinc-200 rounded" />
                  <div className="h-2.5 w-1/2 bg-zinc-100 rounded" />
                </div>
              ))}
            </div>
          </div>
          <div className="md:col-span-2 bg-white rounded-xl border border-zinc-200 p-6 space-y-4 h-[600px] flex flex-col justify-between">
            <div className="space-y-3">
              <div className="h-5 w-48 bg-zinc-200 rounded" />
              <div className="h-3 w-64 bg-zinc-100 rounded" />
              <div className="pt-4 space-y-3">
                <div className="h-16 w-3/4 bg-zinc-50 rounded-xl border border-zinc-100" />
                <div className="h-16 w-2/3 bg-zinc-50 rounded-xl border border-zinc-100 ml-auto" />
                <div className="h-16 w-3/4 bg-zinc-50 rounded-xl border border-zinc-100" />
              </div>
            </div>
            <div className="h-12 w-full bg-zinc-100/60 rounded-xl" />
          </div>
        </div>
      </div>
    );
  }

  if (!data) return null;

  if ((data?.meta?.total_sessions === 0 || !data?.meta?.total_sessions) && !searchTerm) {
    return <EmptyAnalyticsState title="No conversations recorded" message="No conversations have been logged yet for this agent." />;
  }

  const sessions = data.agno_sessions || data.overview_stats?.agno_sessions || [];

  const SYSTEM_METADATA_KEYS = new Set([
    'agentid', 'aiagentid', 'agentkey', 'tenantid', 'teamid',
    'sessionid', 'sessionids', 'token', 'userid', 'user', 'email', 'name', 'phone',
    'username', 'filename', 'v', 'apiurl', 'apibase', 'channel', 'source',
    'mode', 'responsemode', 'streamingenabled', 'launchersize',
    'humanescalation', 'internetsearchenabled',
    'summaryenabled', 'summarymodelid', 'summaryinterval', 'learningenabled',
    'eyecatchertext', 'eyecatchertheme', 'eyecatcherenabled', 'eyecatcheranimation',
    'feedbackenabled', 'campaignsequentialdisplay',
    'osinfo', 'browserinfo', 'devicetype', 'ipaddress', 'ip', 'location',
    'latitude', 'longitude',
    'headercolor', 'userbubblecolor', 'botbubblecolor', 'sendbuttoncolor',
    'primarycolor', 'secondarycolor', 'backgroundcolor', 'textcolor',
    'fontfamily', 'fontsize', 'borderradius', 'position', 'size',
    'showclosebutton', 'showlauncher', 'customparameters', 'iconurl', 'brandlogourl',
    'brandlogo', 'icon', 'ispreview', 'welcomemessage', 'title'
  ]);

  const getFilteredMetadata = (session) => {
    if (!session) return [];
    return Object.entries({ ...(session.metadata || {}), ...(session.extra_data || {}) })
      .filter(([key]) => !SYSTEM_METADATA_KEYS.has(key.toLowerCase().replace(/[^a-z0-9]/g, '')));
  };

  const totalPages = Math.max(1, Math.ceil((data?.meta?.total_sessions || 0) / itemsPerPage));
  const runTime = (run) => toMs(run.raw?.created_at);
  const lastLine = (sess) => {
    const runs = (sess.runs || []).filter(r => r.agent_id !== 'private_note');
    const last = runs[runs.length - 1];
    if (!last) return { text: '', reply: false };
    const reply = !!last.assistant_content;
    const text = String(reply ? last.assistant_content : last.input_content || '')
      .replace(/::IMAGE::\S+/g, '[image]').replace(/\[([^\]\n]{1,80})\]/g, '$1').replace(/[*_#`>]/g, '').replace(/\s+/g, ' ').trim();
    return { text, reply };
  };

  const Bubble = ({ side, label, time, tone, children, footer }) => (
    <div className={cn('flex items-end gap-2', side === 'left' ? 'justify-start' : 'flex-row-reverse')}>
      {tone === 'visitor' ? (
        <Avatar name={resolveDisplayName(selectedSession.user_id, selectedSession.session_id)} seed={selectedSession.session_id} size="sm" />
      ) : tone === 'agent' ? (
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-zinc-900 text-white"><Headphones className="h-3.5 w-3.5" /></div>
      ) : (
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-zinc-200 bg-white text-zinc-600"><Bot className="h-3.5 w-3.5" /></div>
      )}
      <div className={cn('flex max-w-[75%] flex-col', side === 'left' ? 'items-start' : 'items-end')}>
        <div className={cn('mb-1 flex items-center gap-1.5 text-[11px]', side !== 'left' && 'justify-end')}>
          <span className="font-medium text-zinc-700">{label}</span>
          {time && <span className="text-zinc-400">{time}</span>}
        </div>
        <div className={cn(
          'rounded-xl border px-3.5 py-2.5 text-[13px] leading-relaxed break-words [overflow-wrap:anywhere]',
          tone === 'visitor' && 'rounded-bl-sm border-zinc-200 bg-white text-zinc-900',
          tone === 'agent' && 'rounded-br-sm border-zinc-900 bg-zinc-900 text-white',
          tone === 'bot' && 'rounded-br-sm border-zinc-200 bg-zinc-50 text-zinc-900'
        )}>
          {children}
        </div>
        {footer}
      </div>
    </div>
  );

  const BotText = ({ text, picked }) => {
    if (typeof text !== 'string' || /::IMAGE::/.test(text)) return <div className="whitespace-pre-wrap">{renderMessageText(text)}</div>;
    const { body, options } = splitQuickReplies(text);
    return (
      <>
        {body && <div className="markdown-content"><ReactMarkdown>{body}</ReactMarkdown></div>}
        {options.length > 0 && (
          <div className={cn('flex flex-wrap gap-1.5', body && 'mt-2')}>
            {options.map(o => (
              <span key={o} className={cn('inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs',
                picked === o ? 'border-zinc-900 bg-white font-medium text-zinc-900' : 'border-zinc-200 bg-white text-zinc-600')}>
                {picked === o && <Check className="h-3 w-3" />}{o}
              </span>
            ))}
          </div>
        )}
      </>
    );
  };

  const Divider = ({ children }) => (
    <div className="my-5 flex items-center gap-3">
      <div className="h-px flex-1 bg-zinc-200" />
      <span className="rounded-md border border-zinc-200 bg-white px-2 py-0.5 text-[11px] font-medium text-zinc-500">{children}</span>
      <div className="h-px flex-1 bg-zinc-200" />
    </div>
  );

  const CopyId = ({ id }) => {
    const [copied, setCopied] = useState(false);
    return (
      <button type="button" title="Copy session ID"
        onClick={() => { navigator.clipboard?.writeText(id).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); }).catch(() => {}); }}
        className="inline-flex items-center gap-1 rounded px-1 font-mono text-[11px] text-zinc-500 hover:bg-zinc-100 hover:text-zinc-800">
        #{String(id).slice(-6).toUpperCase()} {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
      </button>
    );
  };

  const renderTranscript = () => {
    const currentWelcome = selectedSession.welcome_message || welcomeMessage || data?.welcome_message || "Hello! How can I help you today?";
    const filteredRuns = selectedSession.runs?.filter(run => run.agent_id !== 'private_note') || [];
    const firstRun = filteredRuns[0];
    const hasWelcomeInRuns = firstRun && !firstRun.input_content && Boolean(firstRun.assistant_content);
    const visitorLabel = resolveDisplayName(selectedSession.user_id, selectedSession.session_id);
    const traces = sessionTraces[selectedSession.session_id] || [];
    const rootNodes = traces.filter(n => !n.parent_node_id || n.parent_node_id === 'None' || n.parent_node_id === '');
    const out = [];
    let lastDay = '';

    const day = (ms) => {
      const d = dayLabel(ms);
      if (d && d !== lastDay) {
        lastDay = d;
        out.push(<Divider key={`day_${out.length}`}>{d}</Divider>);
      }
    };

    if (!hasWelcomeInRuns && currentWelcome) {
      const ms = toMs(selectedSession.created_at_iso);
      day(ms);
      out.push(
        <div key="welcome" className="mb-4">
          <Bubble side="right" tone="bot" label="Bot" time={ms ? formatTime(ms) : ''}>
            <BotText text={currentWelcome} />
          </Bubble>
        </div>
      );
    }

    filteredRuns.forEach((run, i, arr) => {
      const ms = runTime(run);
      day(ms);
      const rootNodeForRun = traces.find(node => isTraceMatch(node, run)) || rootNodes[i] || traces[i];
      const traceId = rootNodeForRun?.node_id || `run_${i}`;
      const traceOpen = convoShownTraceIds.has(traceId);
      const isAgent = run.agent_id === 'human_agent';
      if (isAgent && (i === 0 || arr[i - 1].agent_id !== 'human_agent')) out.push(<Divider key={`agent_${i}`}>Agent joined the chat</Divider>);
      const nextInput = arr[i + 1]?.input_content;
      const picked = typeof nextInput === 'string' ? nextInput.trim() : null;

      if (run.input_content) {
        out.push(
          <div key={`in_${i}`} className="mb-4">
            <Bubble side="left" tone="visitor" label={visitorLabel} time={ms ? formatTime(ms) : ''}>
              <div className="whitespace-pre-wrap">{renderMessageText(run.input_content)}</div>
            </Bubble>
          </div>
        );
      }
      if (run.assistant_content) {
        const toggleTrace = () => {
          setConvoShownTraceIds(prev => {
            const next = new Set();
            if (!prev.has(traceId)) next.add(traceId);
            return next;
          });
          if (rootNodeForRun?.node_id) {
            setExpandedTraceNodes(prev => new Set(prev).add(rootNodeForRun.node_id));
          }
        };
        out.push(
          <div key={`out_${i}`} className="mb-4">
            <Bubble side="right" tone={isAgent ? 'agent' : 'bot'} label={isAgent ? 'Agent' : 'Bot'} time={ms ? formatTime(ms) : ''}
              footer={!isAgent && (
                <button type="button" onClick={toggleTrace}
                  className={cn('mt-1 inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px]', traceOpen ? 'bg-zinc-900 text-white' : 'text-zinc-500 hover:bg-zinc-100 hover:text-zinc-800')}>
                  <GitBranch className="h-3 w-3" /> {traceOpen ? 'Hide trace' : 'View trace'}
                  {rootNodeForRun?.latency_ms ? <span className="opacity-70">· {fmtLatency(rootNodeForRun.latency_ms)}</span> : null}
                </button>
              )}>
              {isAgent ? <div className="whitespace-pre-wrap">{renderMessageText(run.assistant_content)}</div> : <BotText text={run.assistant_content} picked={picked} />}
            </Bubble>
            {traceOpen && (
              <div className="mt-2 rounded-xl border border-zinc-200 bg-white p-3">
                {rootNodeForRun ? (
                  <TraceTreeNode node={rootNodeForRun} expandedNodes={expandedTraceNodes} toggleNode={toggleTraceNode} fmtLatency={fmtLatency} prettyJson={prettyJson} />
                ) : (
                  <p className="text-xs text-zinc-500">No trace was recorded for this answer.</p>
                )}
              </div>
            )}
          </div>
        );
      }
    });
    if (selectedSession.handoff_status === 'resolved') out.push(<Divider key="resolved">Conversation resolved</Divider>);
    return out;
  };

  return (
    <div className="animate-slideIn">

      <div ref={panelsRef} className={cn('flex gap-2', (isContactDetail || !panelsHeight) && 'h-[calc(100vh-220px)] min-h-[520px]')}
        style={!isContactDetail && panelsHeight ? { height: panelsHeight } : undefined}>
        {/* List */}
        <aside className="flex w-[320px] shrink-0 flex-col overflow-hidden rounded-xl border border-zinc-200 bg-white">
          <div className="border-b border-zinc-200 p-3">
            <div className="mb-2 flex items-center justify-between px-0.5">
              <h1 className="text-sm font-semibold text-zinc-900">Conversations</h1>
              <span className="text-[11px] tabular-nums text-zinc-500">{data?.meta?.total_sessions || 0} total</span>
            </div>
            <div className="flex h-8 items-center gap-2 rounded-md border border-zinc-200 bg-zinc-50 px-2.5 focus-within:border-zinc-400 focus-within:bg-white">
              <Search className="h-3.5 w-3.5 text-zinc-400" />
              <input
                type="text"
                placeholder="Search by name or session ID"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-transparent text-xs text-zinc-900 outline-none placeholder:text-zinc-400"
              />
              {searchTerm && (
                <button type="button" onClick={() => setSearchTerm('')} aria-label="Clear search" className="text-zinc-400 hover:text-zinc-700">
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto custom-scrollbar">
            {sessions.length === 0 ? (
              <div className="px-6 py-12 text-center">
                <p className="text-sm font-medium text-zinc-700">No matching conversations</p>
                <p className="mt-1 text-xs text-zinc-500">Try another name or session ID.</p>
              </div>
            ) : sessions.map(s => {
              const name = resolveDisplayName(s.user_id, s.session_id);
              const active = selectedSession?.session_id === s.session_id;
              const last = lastLine(s);
              const created = toMs(s.created_at_iso);
              return (
                <button
                  key={s.session_id}
                  type="button"
                  onClick={() => setSelectedSession(s)}
                  className={cn('flex w-full items-start gap-3 border-b border-zinc-100 px-3 py-3 text-left transition-colors', active ? 'bg-zinc-100' : 'hover:bg-zinc-50')}
                >
                  <Avatar name={name} seed={s.session_id} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-[13px] font-medium text-zinc-900">{name}</span>
                      <span className="ml-auto shrink-0 text-[11px] tabular-nums text-zinc-400">{created ? shortAgo(created) : ''}</span>
                    </div>
                    <div className="mt-1 flex items-center gap-1.5">
                      <ChannelIcon session={s} />
                      <span className="truncate text-xs text-zinc-500">{last.text || 'No messages'}</span>
                    </div>
                    <div className="mt-1.5 flex items-center gap-1.5">
                      <Badge variant="outline"><MessagesSquare className="h-3 w-3" /> {s.runs_count} messages</Badge>
                      {s.handoff_status === 'resolved' && <Badge variant="success">Resolved</Badge>}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {(data?.meta?.total_sessions || 0) > itemsPerPage && (
            <div className="flex items-center justify-between gap-2 border-t border-zinc-200 px-3 py-2">
              <Button variant="outline" size="icon" aria-label="Previous page" onClick={() => setConvoPage(p => Math.max(1, p - 1))} disabled={convoPage === 1}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-xs text-zinc-500 tabular-nums">Page <span className="font-medium text-zinc-900">{convoPage}</span> of {totalPages}</span>
              <Button variant="outline" size="icon" aria-label="Next page" onClick={() => setConvoPage(p => p + 1)} disabled={convoPage >= totalPages}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </aside>

        {/* Conversation */}
        <section className="flex min-w-0 flex-1 flex-col overflow-hidden rounded-xl border border-zinc-200 bg-white">
          {selectedSession ? (
            <div key={selectedSession.session_id} className="flex min-h-0 flex-1 flex-col">
              <header className="flex items-center gap-3 border-b border-zinc-200 px-4 py-3">
                <Avatar name={resolveDisplayName(selectedSession.user_id, selectedSession.session_id)} seed={selectedSession.session_id} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <h2 className="truncate text-sm font-semibold text-zinc-900">{resolveDisplayName(selectedSession.user_id, selectedSession.session_id)}</h2>
                    {selectedSession.handoff_status === 'resolved' && <Badge variant="success">Resolved</Badge>}
                  </div>
                  <div className="mt-0.5 flex items-center gap-1.5 text-xs text-zinc-500">
                    <ChannelIcon session={selectedSession} />
                    <span>{CHANNEL_LABELS[channelOf(selectedSession)]}</span>
                    <span className="text-zinc-300">·</span>
                    <span>{selectedSession.runs_count} messages</span>
                    {selectedSession.created_at_iso && (
                      <><span className="text-zinc-300">·</span><span>{new Date(selectedSession.created_at_iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' })}</span></>
                    )}
                    <CopyId id={selectedSession.session_id} />
                  </div>
                </div>
                <div className="relative">
                  <Button variant={showParams ? 'secondary' : 'outline'} size="sm" onClick={() => setShowParams(!showParams)}>
                    <Info className="h-3.5 w-3.5" /> Details
                  </Button>
                  {showParams && (
                    <div className="absolute right-0 top-10 z-40 w-72 rounded-lg border border-zinc-200 bg-white p-3 shadow-lg">
                      <div className="text-[11px] font-medium text-zinc-500">Session ID</div>
                      <div className="mt-1 select-all break-all rounded-md border border-zinc-100 bg-zinc-50 p-2 font-mono text-[11px] text-zinc-600">
                        {selectedSession.session_id}
                      </div>
                      {(() => {
                        const filteredMeta = getFilteredMetadata(selectedSession);
                        if (filteredMeta.length === 0) return <p className="mt-3 text-xs text-zinc-500">No custom data for this conversation.</p>;
                        return (
                          <dl className="mt-3 space-y-1.5">
                            {filteredMeta.map(([key, value]) => (
                              <div key={key} className="grid grid-cols-[96px_1fr] gap-2 text-xs">
                                <dt className="truncate text-zinc-500">{key}</dt>
                                <dd className="break-words text-zinc-900">{typeof value === 'object' ? JSON.stringify(value) : String(value)}</dd>
                              </div>
                            ))}
                          </dl>
                        );
                      })()}
                    </div>
                  )}
                </div>
              </header>
              <div className="flex-1 overflow-y-auto bg-zinc-50/60 px-5 py-4 custom-scrollbar">
                {renderTranscript()}
              </div>
            </div>
          ) : (
            <div className="flex flex-1 flex-col items-center justify-center p-8 text-center">
              <MessagesSquare className="h-8 w-8 text-zinc-300" />
              <p className="mt-3 text-sm font-medium text-zinc-700">Pick a conversation</p>
              <p className="mt-1 max-w-xs text-xs text-zinc-500">See the full chat, and open "View trace" under any bot answer to see the steps, tools and timings behind it.</p>
            </div>
          )}
        </section>
      </div>

      <style>{`
        .markdown-content p { margin: 0; }
        .markdown-content p + p { margin-top: 0.5em; }
        .markdown-content ul, .markdown-content ol { margin: 0.25em 0; padding-left: 1.25em; }
        .markdown-content ul { list-style: disc; }
        .markdown-content ol { list-style: decimal; }
        .markdown-content a { text-decoration: underline; }
      `}</style>
    </div>
  );
}
